import {
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { notificationSchema, type Notification } from "./schemas";

export type NotificationState =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "error"; error: string }
  | { status: "success"; data: Notification };

/**
 * One inbox document, live, for the page a push click opens.
 *
 * A missing document and somebody else's document look the same to the
 * reader: the rules deny a get on another user's inbox, and saying "not
 * found" for both avoids telling a guessed id apart from a real one.
 */
export function useNotification(
  uid: string,
  notificationId: string,
): NotificationState {
  const [state, setState] = useState<NotificationState>({ status: "loading" });

  useEffect(
    () =>
      onSnapshot(
        doc(db, "users", uid, "notifications", notificationId),
        (snapshot) => {
          if (!snapshot.exists()) {
            setState({ status: "not-found" });
            return;
          }
          const parsed = notificationSchema.safeParse(snapshot.data());
          setState(
            parsed.success
              ? {
                  status: "success",
                  data: { ...parsed.data, id: snapshot.id },
                }
              : {
                  status: "error",
                  error: "This notification could not be displayed.",
                },
          );
        },
        (error) =>
          setState(
            (error as { code?: string }).code === "permission-denied"
              ? { status: "not-found" }
              : {
                  status: "error",
                  error:
                    error instanceof Error
                      ? error.message
                      : "Failed to load notification",
                },
          ),
      ),
    [uid, notificationId],
  );

  return state;
}

/** Flip readAt, the one field the rules let the owner write. */
export async function markNotificationRead(
  uid: string,
  notification: Notification,
): Promise<void> {
  if (notification.readAt !== null) return;
  await updateDoc(doc(db, "users", uid, "notifications", notification.id), {
    readAt: serverTimestamp(),
  });
}
